import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import SouthIcon from "@mui/icons-material/South";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { DateTime } from "luxon";

import { formatMoney } from "../../DashboardView/dashboardHelpers";
import { SessionSummary } from "../../DashboardView/components/RescheduleDialog";

// A wall date + time as the fake-UTC stamp SessionSummary reads.
const wallStamp = (date, time) => `${date}T${time}:00.000Z`;
const dayOf = (iso) => DateTime.fromISO(iso).toFormat("ccc d LLL");
const withCoach = (name) => (name ? ` with ${name}` : "");

/**
 * What confirming does, as three lines the client can check one by one: the lesson that
 * goes, the lesson that takes its place and what pays for it, and where that leaves the
 * pack (or the money). Replaces a paragraph that said the same thing less plainly.
 */
function WhatHappens({ info, target, currency }) {
  const { from, pack } = info;
  const slot = target.slot;
  const isMoney = info.mode === "MONEY_MOVE";
  const ownTarget = !!slot.ownBookingId;
  const packName = pack?.name || "your package";

  const paid = isMoney ? `the ${formatMoney(info.creditAmount, currency)} you paid` : `your ${packName}`;
  const takes = ownTarget ? `is paid with ${paid}` : `is booked with ${paid}`;

  // The pack neither gains nor loses a session. Its spare count is only worth saying when
  // there is one: a fully booked pack reads "0 left", which sounds like bad news.
  const spare = pack?.sessionsRemaining > 0 ? ` · ${pack.sessionsRemaining} spare session${pack.sessionsRemaining === 1 ? "" : "s"} left` : "";
  const balance = isMoney ? "Nothing to pay — the amount you paid covers it exactly" : `No session is used up on your ${packName}${spare}`;

  const rows = [
    {
      icon: <CancelOutlinedIcon sx={{ color: "error.main" }} />,
      text: `${dayOf(from.date)} · ${from.time}${withCoach(from.trainerName)} is cancelled`
    },
    {
      icon: <CheckCircleOutlineIcon sx={{ color: "primary.main" }} />,
      text: `${dayOf(target.date)} · ${slot.time}${withCoach(slot.trainerName)} ${takes}`
    },
    { icon: <ConfirmationNumberOutlinedIcon sx={{ color: "text.secondary" }} />, text: balance }
  ];

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        What happens when you confirm
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {rows.map((row, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ display: "flex", "& svg": { fontSize: "1.15rem" }, mt: "0.05rem" }}>{row.icon}</Box>
            <Typography variant="body2">{row.text}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function MoveBody({ info, target, currency }) {
  const from = info.from;
  const ownTarget = !!target.slot.ownBookingId;
  return (
    <Box>
      <SessionSummary
        label="From · cancelled"
        startDate={wallStamp(from.date, from.time)}
        time={from.time}
        duration={from.duration}
        trainerName={from.trainerName}
        locationName={from.locationName}
        cancelled
      />
      <Box display="flex" justifyContent="center" py={0.5}>
        <SouthIcon sx={{ fontSize: "1.25rem", color: "text.secondary" }} />
      </Box>
      <Box mb={2}>
        <SessionSummary
          label="To"
          startDate={wallStamp(target.date, target.slot.time)}
          time={target.slot.time}
          duration={target.slot.duration}
          trainerName={target.slot.trainerName}
          locationName={target.slot.locationName}
          badge={ownTarget ? "Booked for you · not yet paid" : undefined}
          highlight
        />
      </Box>
      <WhatHappens info={info} target={target} currency={currency} />
    </Box>
  );
}

function MoveActions({ reschedule, submitting, grow = false }) {
  return (
    <>
      <Button onClick={reschedule.close} disabled={submitting} sx={grow ? { flex: 1 } : undefined}>
        Back
      </Button>
      <Button variant="contained" onClick={reschedule.confirm} disabled={submitting} sx={{ color: "common.white", ...(grow && { flex: 2 }) }}>
        {submitting ? <CircularProgress size={18} /> : "Confirm move"}
      </Button>
    </>
  );
}

// Desktop: a dialog. Phone: a bottom sheet, like the page's filter sheets — this confirm
// is short, and a full screen left the buttons stranded under empty space.
function LessonRescheduleDialog({ reschedule, currency, isMobile }) {
  const { target, info, submitting } = reschedule;
  const ready = !!target && !!info?.from;
  const title = target?.slot?.ownBookingId ? "Confirm move" : "Confirm new time";

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={!!target}
        onClose={reschedule.close}
        PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: "90vh", px: 2.5, pt: 2, pb: 3 } }}
      >
        <Box sx={{ width: "2.5rem", height: "0.25rem", borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 2 }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700 }}>{title}</Typography>
          <IconButton size="small" onClick={reschedule.close} disabled={submitting} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: "auto" }}>{ready && <MoveBody info={info} target={target} currency={currency} />}</Box>
        <Box sx={{ display: "flex", gap: 1, mt: 2.5 }}>
          <MoveActions reschedule={reschedule} submitting={submitting} grow />
        </Box>
      </Drawer>
    );
  }

  return (
    <Dialog open={!!target} onClose={reschedule.close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pr: 1.5 }}>
        {title}
        <IconButton onClick={reschedule.close} disabled={submitting} aria-label="Close">
          <CloseIcon sx={{ fontSize: "1.25rem" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>{ready && <MoveBody info={info} target={target} currency={currency} />}</DialogContent>
      <DialogActions>
        <MoveActions reschedule={reschedule} submitting={submitting} />
      </DialogActions>
    </Dialog>
  );
}

export default LessonRescheduleDialog;
